import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import Content from "../../layout/content/Content";
import Head from "../../layout/head/Head";
import { Card, Row, Col, Button, Spinner } from "reactstrap";
import {
  Block,
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import { Table, InputNumber, message, Tag } from "antd";
import {
  ScheduleOutlined,
  DownOutlined,
  RightOutlined,
  UnlockOutlined,
} from "@ant-design/icons";
import Swal from "sweetalert2";
import workOrderApis from "../../api/WorkOrderManagement/WorkOrderApis";
import workExecutionApis from "../../api/WorkOrderManagement/WorkExecutionApis";

const WorkExecutionRelease: React.FC = () => {
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  const [workOrder, setWorkOrder] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [workLines, setWorkLines] = useState<any[]>([]);

  useEffect(() => {
    if (workOrderId) {
      loadWorkOrderData(workOrderId);
    }
  }, [workOrderId]);

  const loadWorkOrderData = async (id: string) => {
    try {
      setLoading(true);
      const response: any = await workOrderApis.getWorkOrderById(id);

      if (response && response.status && response.data) {
        const apiData = response.data;
        setWorkOrder(apiData);

        // Map work order lines for release
        const mappedLines =
          apiData.workOrderLines?.map((line: any) => ({
            id: line.workOrderLineId?.toString() || "",
            workLineNumber: line.workOrderLineId || "",
            workLineTitle: line.taskTitle || "",
            description: line.description || "",
            approvedQuantity: line.quantity || 0,
            rate: line.rate || 0,
            cumulativeExecuted: line.totalExecutedQty || 0,
            onHoldQty: line.totalOnHoldQty || 0, // Available to release
            remainingQuantity:
              line.quantity -
              line.totalExecutedQty -
              line.totalApprovalExecutedQty,
            releaseQuantity: 0, // User input
            uom: line.uomName || "",
            error: "",
            // Stage breakdown for stage-wise release
            stages:
              line.stages?.map((stage: any) => ({
                workOrderLineStageId: stage.workOrderLineStageId,
                stageTitle: stage.stageTitle || "",
                splitPercentage: stage.splitPercentage || 0,
                approvedQty: stage.allocatedQty || 0,
                cumulativeExecutedQty: stage.totalExecutedQty || 0,
                onHoldQty: stage.totalOnHoldQty || 0, // Available to release
                remainingQty:
                  stage.allocatedQty > 0
                    ? stage.allocatedQty -
                      (stage.totalExecutedQty + stage.totalApprovalExecutedQty)
                    : 0,
                releaseQuantity: 0, // User input
                error: "", // Validation error
              })) || [],
          })) || [];

        setWorkLines(mappedLines);
      } else {
        message.error("Failed to load work order data");
        navigate(-1);
      }
    } catch (error) {
      console.error("Error loading work order:", error);
      message.error("Failed to load work order data");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseQuantityChange = (lineId: string, value: number) => {
    setWorkLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id !== lineId) return line;

        // Validate: release qty should not exceed onHold qty
        const hasError = value > line.onHoldQty;

        return {
          ...line,
          releaseQuantity: value,
          error: hasError
            ? `Cannot exceed on-hold quantity (${line.onHoldQty})`
            : "",
        };
      })
    );
  };

  const handleStageReleaseQuantityChange = (
    lineId: string,
    stageId: number,
    value: number
  ) => {
    setWorkLines((prevLines) =>
      prevLines.map((line) => {
        if (line.id !== lineId) return line;

        const updatedStages = line.stages.map((stage: any) => {
          if (stage.workOrderLineStageId !== stageId) return stage;

          // Validate: release qty should not exceed onHold qty
          const hasError = value > stage.onHoldQty;

          return {
            ...stage,
            releaseQuantity: value,
            error: hasError
              ? `Cannot exceed on-hold quantity (${stage.onHoldQty})`
              : "",
          };
        });

        return {
          ...line,
          stages: updatedStages,
        };
      })
    );
  };

  const validateForm = () => {
    // Check for release data:
    // - Lines WITH stages: check if any stage has releaseQuantity > 0
    // - Lines WITHOUT stages: check if releaseQuantity > 0
    const hasStageRelease = workLines.some(
      (line) =>
        line.stages &&
        line.stages.length > 0 &&
        line.stages.some((stage: any) => stage.releaseQuantity > 0)
    );
    const hasLineRelease = workLines.some(
      (line) =>
        (!line.stages || line.stages.length === 0) && line.releaseQuantity > 0
    );

    if (!hasStageRelease && !hasLineRelease) {
      message.error("Please enter Release quantity for at least one work line");
      return false;
    }

    // Check for validation errors in lines
    const hasLineValidationErrors = workLines.some((line) => line.error);
    if (hasLineValidationErrors) {
      message.error(
        "Please fix validation errors in work lines before submitting"
      );
      return false;
    }

    // Check for validation errors in stages
    const hasStageValidationErrors = workLines.some(
      (line) =>
        line.stages &&
        line.stages.length > 0 &&
        line.stages.some((stage: any) => stage.error)
    );
    if (hasStageValidationErrors) {
      message.error(
        "Please fix validation errors in work line stages before submitting"
      );
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSubmitting(true);

      // Build lineUpdates based on whether line has stages or not
      // BE expects: lines without stages -> { workOrderLineId, releaseQuantity }
      //            lines with stages -> { workOrderLineId, stages: [{ workOrderLineStageId, releaseQuantity }] }
      const lineUpdates = workLines
        .filter((line) => {
          if (line.stages && line.stages.length > 0) {
            return line.stages.some((stage: any) => stage.releaseQuantity > 0);
          }
          return line.releaseQuantity > 0;
        })
        .map((line) => {
          if (line.stages && line.stages.length > 0) {
            // Line with stages: group stages under the line
            const stages = line.stages
              .filter((stage: any) => stage.releaseQuantity > 0)
              .map((stage: any) => ({
                workOrderLineStageId: stage.workOrderLineStageId,
                releaseQuantity: stage.releaseQuantity,
              }));

            return {
              workOrderLineId: parseInt(line.id),
              stages: stages,
            };
          }
          // Line without stages: direct releaseQuantity
          return {
            workOrderLineId: parseInt(line.id),
            releaseQuantity: line.releaseQuantity,
          };
        });

      // Get current date in YYYY-MM-DD format
      const currentDate = new Date().toISOString().split("T")[0];

      const releaseData = {
        workOrderId: workOrder.workOrderId,
        releaseDate: currentDate,
        lineUpdates: lineUpdates,
      };

      const response: any = await workExecutionApis.releaseExecution(
        releaseData
      );

      if (response && response.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: "Work execution release submitted successfully",
        }).then(() => {
          const searchParams = new URLSearchParams(location.search);
          const flowType = searchParams.get("flowType");
          const flowTypeQuery = flowType ? `?flowType=${flowType}` : "";
          navigate(`/work-order-view/${workOrderId}${flowTypeQuery}`);
        });
      } else {
        throw new Error(
          response?.message || "Failed to submit release request"
        );
      }
    } catch (error: any) {
      console.error("Error submitting release request:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit release request. Please try again.";
      message.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const hasReleaseData = () => {
    return (
      workLines.some(
        (line) =>
          line.stages &&
          line.stages.length > 0 &&
          line.stages.some((s: any) => s.releaseQuantity > 0)
      ) ||
      workLines.some(
        (line) =>
          (!line.stages || line.stages.length === 0) && line.releaseQuantity > 0
      )
    );
  };

  const releaseLinesColumns = [
    {
      title: "Line #",
      dataIndex: "workLineNumber",
      key: "workLineNumber",
      width: 80,
      fixed: "left" as const,
    },
    {
      title: "Work Line Title",
      dataIndex: "workLineTitle",
      key: "workLineTitle",
      width: 200,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: 200,
    },
    {
      title: "Approved Qty",
      dataIndex: "approvedQuantity",
      key: "approvedQuantity",
      width: 120,
      render: (qty: number, record: any) => (
        <div>
          {qty?.toLocaleString()} {record.uom}
        </div>
      ),
    },
    {
      title: "Cumulative Executed",
      dataIndex: "cumulativeExecuted",
      key: "cumulativeExecuted",
      width: 150,
      render: (qty: number, record: any) => (
        <div>
          {qty?.toLocaleString()} {record.uom}
        </div>
      ),
    },
    {
      title: "OnHold Qty",
      dataIndex: "onHoldQty",
      key: "onHoldQty",
      width: 130,
      render: (qty: number, record: any) => (
        <div style={{ color: "#fa8c16", fontWeight: 500 }}>
          {qty?.toLocaleString()} {record.uom}
        </div>
      ),
    },
    {
      title: "Remaining Qty",
      dataIndex: "remainingQuantity",
      key: "remainingQuantity",
      width: 120,
      render: (qty: number, record: any) => (
        <div>
          {qty?.toLocaleString()} {record.uom}
        </div>
      ),
    },
    {
      title: "Release Qty",
      key: "releaseQuantity",
      width: 150,
      render: (_: any, record: any) => {
        if (record.stages && record.stages.length > 0) {
          const totalStageReleaseQty = record.stages.reduce(
            (sum: number, stage: any) => sum + (stage.releaseQuantity || 0),
            0
          );
          return (
            <div style={{ textAlign: "center" }}>
              <Tag color="purple">
                <ScheduleOutlined /> Stage-wise
              </Tag>
              {totalStageReleaseQty > 0 && (
                <div
                  style={{
                    fontSize: "12px",
                    marginTop: "4px",
                    color: "#52c41a",
                  }}
                >
                  Qty: {totalStageReleaseQty.toLocaleString()}
                </div>
              )}
            </div>
          );
        }
        return (
          <div>
            <InputNumber
              min={0}
              value={record.releaseQuantity}
              onChange={(value) =>
                handleReleaseQuantityChange(record.id, value || 0)
              }
              style={{ width: "100%" }}
              status={record.error ? "error" : undefined}
              disabled={record.onHoldQty <= 0}
            />
            {record.error && (
              <div
                style={{ color: "#ff4d4f", fontSize: "12px", marginTop: "4px" }}
              >
                {record.error}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  if (loading) {
    return (
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Loading...</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <div className="text-center py-5">
            <Spinner color="primary" />
          </div>
        </Block>
      </Content>
    );
  }

  if (!workOrder) {
    return (
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle>Work Order Not Found</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
      </Content>
    );
  }

  return (
    <>
      <Head title="Release Execution" />
      <Content>
        <BlockHead size="sm">
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle page>
                <UnlockOutlined style={{ marginRight: 8 }} />
                Release Execution - {workOrder.workOrderNumber}
              </BlockTitle>
              <div className="nk-block-des text-soft">
                <p>Release on-hold work execution quantities</p>
              </div>
            </BlockHeadContent>
            <BlockHeadContent>
              <Button
                color="light"
                outline
                className="bg-white d-none d-sm-inline-flex"
                onClick={() => navigate(-1)}
              >
                <Icon name="arrow-left"></Icon>
                <span>Back</span>
              </Button>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>

        <Block>
          <Card className="card-bordered">
            <div className="card-inner">
              {/* Header Information */}
              <Row
                className="g-3"
                style={{
                  marginBottom: 24,
                  padding: 16,
                  background: "#f6ffed",
                  borderRadius: 8,
                }}
              >
                <Col md="3">
                  <div>
                    <strong>WO #:</strong> {workOrder.workOrderNumber}
                  </div>
                </Col>
                <Col md="3">
                  <div>
                    <strong>Contractor:</strong> {workOrder.contractorName}
                  </div>
                </Col>
                <Col md="3">
                  <div>
                    <strong>Site:</strong> {workOrder.siteName}
                  </div>
                </Col>
                <Col md="3">
                  <div>
                    <strong>Total Value:</strong> ₹
                    {workOrder.grossValue?.toLocaleString() || "0"}
                  </div>
                </Col>
              </Row>

              {/* Work Lines Summary */}
              {workLines.length > 0 && (
                <Row
                  style={{
                    marginBottom: "3%",
                    padding: 12,
                    background: "#e6fffb",
                    borderRadius: 6,
                    marginTop: "2%",
                  }}
                >
                  <Col md="12">
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <strong>Work Lines Summary:</strong>
                      <div>
                        <span>
                          Total Lines: <strong>{workLines.length}</strong>
                        </span>
                        <span style={{ marginLeft: 20 }}>
                          Total OnHold Qty:{" "}
                          <strong style={{ color: "#fa8c16" }}>
                            {workLines
                              .reduce((sum, line) => sum + line.onHoldQty, 0)
                              .toLocaleString()}
                          </strong>
                        </span>
                        <span style={{ marginLeft: 20 }}>
                          Remaining Qty:{" "}
                          <strong>
                            {workLines
                              .reduce(
                                (sum, line) => sum + line.remainingQuantity,
                                0
                              )
                              .toLocaleString()}
                          </strong>
                        </span>
                      </div>
                    </div>
                  </Col>
                </Row>
              )}

              {/* Work Lines Grid */}
              <div style={{ marginBottom: 16 }}>
                <h6 style={{ marginBottom: 16 }}>
                  <strong>
                    Work Lines - Release ({workLines.length} lines)
                  </strong>
                </h6>
                <Table
                  columns={releaseLinesColumns}
                  dataSource={workLines}
                  rowKey="id"
                  pagination={false}
                  scroll={{ x: 1200 }}
                  size="small"
                  bordered
                  expandable={{
                    expandedRowRender: (record: any) => {
                      if (!record.stages || record.stages.length === 0)
                        return null;

                      return (
                        <div
                          style={{
                            padding: "12px 16px",
                            backgroundColor: "#f6ffed",
                          }}
                        >
                          <div
                            style={{
                              marginBottom: 12,
                              fontWeight: 600,
                              color: "#52c41a",
                            }}
                          >
                            <ScheduleOutlined style={{ marginRight: 8 }} />
                            Stage-wise Release ({record.stages.length} stages)
                          </div>
                          <Table
                            dataSource={record.stages.map(
                              (stage: any, idx: number) => ({
                                ...stage,
                                key: idx,
                              })
                            )}
                            columns={[
                              {
                                title: "Stage",
                                dataIndex: "stageTitle",
                                key: "stageTitle",
                                width: 200,
                              },
                              {
                                title: "Percentage",
                                dataIndex: "splitPercentage",
                                key: "splitPercentage",
                                width: 100,
                                render: (val: number) => (
                                  <Tag color="blue">{val}%</Tag>
                                ),
                              },
                              {
                                title: "Approved Qty",
                                dataIndex: "approvedQty",
                                key: "approvedQty",
                                width: 120,
                              },
                              {
                                title: "Cumulative Executed Qty",
                                dataIndex: "cumulativeExecutedQty",
                                key: "cumulativeExecutedQty",
                                width: 150,
                              },
                              {
                                title: "OnHold Qty",
                                dataIndex: "onHoldQty",
                                key: "onHoldQty",
                                width: 130,
                                render: (val: number) => (
                                  <span
                                    style={{
                                      color: "#fa8c16",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {val?.toLocaleString()}
                                  </span>
                                ),
                              },
                              {
                                title: "Remaining Qty",
                                dataIndex: "remainingQty",
                                key: "remainingQty",
                                width: 120,
                              },
                              {
                                title: "Release Qty",
                                key: "releaseQuantity",
                                width: 120,
                                render: (_: any, stage: any) => (
                                  <div>
                                    <InputNumber
                                      min={0}
                                      value={stage.releaseQuantity}
                                      onChange={(value) =>
                                        handleStageReleaseQuantityChange(
                                          record.id,
                                          stage.workOrderLineStageId,
                                          value || 0
                                        )
                                      }
                                      style={{ width: "100%" }}
                                      status={stage.error ? "error" : undefined}
                                      disabled={stage.onHoldQty <= 0}
                                    />
                                    {stage.error && (
                                      <div
                                        style={{
                                          color: "#ff4d4f",
                                          fontSize: "12px",
                                          marginTop: "4px",
                                        }}
                                      >
                                        {stage.error}
                                      </div>
                                    )}
                                  </div>
                                ),
                              },
                            ]}
                            pagination={false}
                            size="small"
                            bordered
                            summary={() => {
                              const totalReleaseQty = record.stages.reduce(
                                (sum: number, s: any) =>
                                  sum + (s.releaseQuantity || 0),
                                0
                              );
                              return (
                                <Table.Summary fixed>
                                  <Table.Summary.Row
                                    style={{ backgroundColor: "#f6ffed" }}
                                  >
                                    <Table.Summary.Cell index={0}>
                                      <strong>Total</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={1}>
                                      -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={2}>
                                      -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={3}>
                                      -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4}>
                                      -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={5}>
                                      -
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={6}>
                                      <strong style={{ color: "#52c41a" }}>
                                        {totalReleaseQty.toLocaleString()}
                                      </strong>
                                    </Table.Summary.Cell>
                                  </Table.Summary.Row>
                                </Table.Summary>
                              );
                            }}
                          />
                        </div>
                      );
                    },
                    expandIcon: ({ expanded, onExpand, record }: any) => {
                      const hasStages =
                        record.stages && record.stages.length > 0;
                      if (!hasStages) return null;

                      return expanded ? (
                        <DownOutlined
                          onClick={(e) => onExpand(record, e)}
                          style={{ color: "#52c41a", cursor: "pointer" }}
                        />
                      ) : (
                        <RightOutlined
                          onClick={(e) => onExpand(record, e)}
                          style={{ color: "#52c41a", cursor: "pointer" }}
                        />
                      );
                    },
                    rowExpandable: (record: any) =>
                      !!(record.stages && record.stages.length > 0),
                  }}
                />
              </div>

              {/* Action Buttons */}
              <div
                style={{
                  textAlign: "right",
                  borderTop: "1px solid #f0f0f0",
                  paddingTop: 16,
                  marginTop: 16,
                }}
              >
                <Button
                  color="light"
                  onClick={() => navigate(-1)}
                  style={{ marginRight: 8 }}
                  disabled={submitting}
                >
                  Cancel
                </Button>
                <Button
                  color="success"
                  onClick={handleSubmit}
                  disabled={
                    submitting ||
                    !hasReleaseData() ||
                    workLines.some((line) => line.error) ||
                    workLines.some(
                      (line) =>
                        line.stages &&
                        line.stages.length > 0 &&
                        line.stages.some((stage: any) => stage.error)
                    )
                  }
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" /> Submitting...
                    </>
                  ) : (
                    <>
                      <UnlockOutlined style={{ marginRight: 4 }} />
                      Submit Release
                    </>
                  )}
                </Button>
              </div>
            </div>
          </Card>
        </Block>
      </Content>
    </>
  );
};

export default WorkExecutionRelease;
